import { consume } from '@lit/context';
import { localized, t } from '@weavedev/lit-i18next';
import { LitElement, css, html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { Ref, createRef, ref } from 'lit/directives/ref.js';

import { PrintOptionCollapsibleDto } from '@/api-sdk';
import { FgPersonalisation } from '@/components';
import { iconUpload } from '@/components/icons';
import {
  ConfiguratorContextType,
  PersonalisationType,
  configuratorGlobal,
  personalisation,
} from '@/context';
import { BREAKPOINT_SMALL_PX } from '@/settings';
import { baseStyles, buttonStyles } from '@/styles';
import { ActionButton, PersonalisationTypes } from '@/types';
import { getPersonalisationData } from '@/utils';

import '@/components/configurator/step-collapsible/fg-collapsible-icon-button';
import '@/components/fg-print-option-summary';
import '@/components/personalisation/fg-personalisation';

@customElement('fg-collapsible-print-option-personalisation')
@localized()
export class FgCollapsiblePrintOptionPersonalisation extends LitElement {
  fgCollapsiblePersonalisationDialogRef: Ref<FgPersonalisation> = createRef();

  @property({ type: Object })
  printOption?: PrintOptionCollapsibleDto;

  @state()
  personalisationData?: PersonalisationType;

  @consume({ context: configuratorGlobal.context, subscribe: true })
  configuratorContext!: ConfiguratorContextType;

  @consume({ context: personalisation.context, subscribe: true })
  personalisationContext!: PersonalisationType[];

  render() {
    if (this.printOption === undefined) {
      return null;
    }

    const selectedPrintOptionCode = this.printOption?.selectedPrintOption?.code;
    const { configurationId } = this.configuratorContext;

    if (!configurationId) {
      this.personalisationData = personalisation.getDataWithCode(
        this.personalisationContext,
        selectedPrintOptionCode || undefined,
      );
    }

    const { type, uploadedFileName, textPersonalization } =
      this.personalisationData || {};

    const hasPersonalisation =
      Boolean(uploadedFileName) || Boolean(textPersonalization);

    const personalisationTypes: PersonalisationTypes = {
      text: {
        title: t('labels.text'),
        description: textPersonalization?.text,
        fontFamily: textPersonalization?.font,
      },
      image: {
        title: t('labels.file'),
        logo: uploadedFileName || undefined,
      },
      text_image: {
        title: t('labels.fileAndText'),
        logo: uploadedFileName || undefined,
        description: textPersonalization?.text,
        fontFamily: textPersonalization?.font,
      },
    };

    return html`
      <div>
        ${type && hasPersonalisation && selectedPrintOptionCode
          ? html`
              <p>
                <span>
                  ${personalisationTypes[type]?.logo
                    ? html`
                        ${personalisationTypes[type].logo}
                      `
                    : nothing}
                  ${personalisationTypes[type].description
                    ? html`
                        ${personalisationTypes[type].logo ? '-' : nothing}
                        <span
                          style="
                        ${personalisationTypes[type]?.fontFamily
                            ? `font-family:${personalisationTypes[type].fontFamily}`
                            : nothing}
                      "
                        >
                          ${personalisationTypes[type].description}
                        </span>
                      `
                    : nothing}
                </span>

                <button
                  class="button-link"
                  @click=${() =>
                    personalisation.providerRemoveEvent(
                      this,
                      selectedPrintOptionCode,
                    )}
                  title="${t('actions.deleteLogoAndOrText') || ''}"
                >
                  <span aria-hidden="true" class="delete-text">
                    ${t('actions.delete')}
                  </span>
                  <fg-collapsible-icon-button
                    actionButton=${ActionButton.DELETE}
                    asDiv
                  ></fg-collapsible-icon-button>
                </button>
              </p>
            `
          : html`
              <span>${t('labels.uploadLogoTextAfterOrder')}</span>
              <button
                class="button button-primary button-small button-icon"
                title=${t('labels.submitLogoAndTextNow') || ''}
                @click=${this.handleLogoSupplyChoice}
              >
                ${t('actions.uploadDirectly')}
                <span aria-hidden="true">${iconUpload}</span>
              </button>
            `}
      </div>

      ${this.printOption?.selectedPrintOption
        ? html`
            <fg-personalisation
              ${ref(this.fgCollapsiblePersonalisationDialogRef)}
              .hasColorChoice=${this.printOption.selectedPrintOption
                ?.hasColorChoice}
              .printOption=${this.printOption.selectedPrintOption}
            >
              <fg-print-option-summary
                slot="print-option-summary"
                .option=${this.printOption.selectedPrintOption}
              ></fg-print-option-summary>
            </fg-personalisation>
          `
        : nothing}
    `;
  }

  handleLogoSupplyChoice() {
    if (this.printOption?.selectedPrintOption) {
      this.fgCollapsiblePersonalisationDialogRef.value?.openDialog();
      return;
    }
  }

  async getPersonalisationFromConfiguration(configurationID: string) {
    getPersonalisationData(this, configurationID);
    this.requestUpdate();
  }

  firstUpdated() {
    const { configurationId } = this.configuratorContext;

    if (configurationId && this.printOption) {
      this.getPersonalisationFromConfiguration(configurationId);
      configuratorGlobal.updateGlobalProviderEvent(this, {
        ...this.configuratorContext,
        configurationId: '',
      });
    }

    this.announceSelectedPrintOption();
  }

  private announceSelectedPrintOption() {
    if (!this.printOption?.selectedPrintOption) return;

    const { colorCount, position, printTechnique, maxSurface } =
      this.printOption.selectedPrintOption;
    const printingNumber = this.printOption.title.match(/\d+/)?.[0] || '';

    const announcement = t('labels.printingOptionSelected', {
      number: printingNumber,
      position: position || '',
      maxSurface: maxSurface
        ? t('labels.maxPrintSurface', { surface: maxSurface })
        : '',
      technique: printTechnique || '',
      colors: colorCount
        ? t('labels.colors', { count: colorCount })
        : colorCount === null
          ? t('labels.fullColor')
          : '',
    });

    this.dispatchEvent(
      new CustomEvent('handle-announcement', {
        composed: true,
        detail: {
          announcement,
        },
      }),
    );
  }

  static styles = [
    baseStyles,
    buttonStyles,
    css`
      div {
        align-items: center;
        background-color: var(--fg-gray--100, var(--fg-fallback-gray--100));
        border-radius: var(--fg-borderradius, var(--fg-fallback-borderradius));
        display: flex;
        flex-wrap: wrap;
        justify-content: space-between;
        margin: 8px 0 16px;
        padding: 12px;
      }
      span:not(.delete-text) {
        color: var(--fg-gray--800, var(--fg-fallback-gray--800));
        font-size: 1rem;
        font-style: italic;
      }
      .button-link {
        display: inline-flex;
        gap: 8px;
        margin-left: 4px;
      }
      .button-small {
        display: block;
        margin-top: 8px;
        width: 100%;
        @media screen and (min-width: ${BREAKPOINT_SMALL_PX}px) {
          display: inline-flex;
          margin-top: 0;
          width: auto;
        }
      }
      .button-small span {
        color: white;
      }
    `,
  ];
}
